'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { PUBLIC_PATH_REGEX_SOURCE } from '@/lib/sonarly-public-paths';

declare global {
  interface Window {
    Sonarly?: { start?: () => void; stop?: () => void; [key: string]: unknown };
  }
}

/**
 * Stops the Sonarly session-replay tracker the moment the SPA navigates off the public
 * marketing set. The tracker is started (pre-hydration) only on public paths, but Next.js
 * App Router navigations never reload the document — so without this, a user who lands on
 * `/` (tracker starts) and then client-side navigates into `/login` and the authenticated
 * app would have their entire credential-bearing session recorded and shipped to sonarly.dev.
 * The PUBLIC regex is single-sourced with the init gate in `app/layout.tsx`.
 */
export function SonarlyGuard() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isPublic = new RegExp(PUBLIC_PATH_REGEX_SOURCE).test(pathname);
    if (!isPublic && window.Sonarly) {
      try {
        window.Sonarly.stop?.();
      } catch {
        // best-effort — tracker may not have finished loading
      }
      // Drop the handle so the still-loading tracker's onload handler can't (re)start it
      // and nothing records after we leave the public routes.
      window.Sonarly = undefined;
    }
  }, [pathname]);

  return null;
}
